import { Alert } from '@/global/components/Alert'
import { Grid } from '@mui/material'
import { FormFieldSelect, FormFile } from 'global/components/Form'
import ContentGuide from 'global/guides/ContentGuide/ContentGuide'
import { ChangeEvent, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { regrasCalculo, tabelaHorarios } from 'service/api'
import { useStore } from 'store'
import {
    lerCsvFuncionarios,
    lerXlsFuncionarios,
} from 'templates/Cadastro/Funcionarios/service/ler'
import { useImportacaoStore } from 'templates/Cadastro/Funcionarios/store'
import { TFuncionario } from 'templates/Cadastro/Funcionarios/store/types'
import { Endereco } from '../DadosPessoais/Endereco'

const extensoesXls = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet',
]
const extensoesCsv = ['text/csv']

export default function FormImportar() {
    const empresaId = useStore(state => state.companyId)
    const { watch } = useFormContext()
    const Cidade = watch('Cidade')

    const { data: regras_calculo } = regrasCalculo.useFetch({
        queryParams: { empresaId },
    })
    const { data: tabelas_horario } = tabelaHorarios.useFetch({
        queryParams: { empresaId },
    })

    const { setLinhaInicial, setCidade, setErro, setImports } =
        useImportacaoStore(store => store.actions)

    const { erro, funcionariosImportar } = useImportacaoStore(
        store => store.state,
    )

    useEffect(() => {
        if (Cidade) setCidade(Cidade)
    }, [Cidade])

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            setErro(null)
            return setImports(undefined)
        }
        const tipo = event.target.files[0].type

        if (!extensoesXls.concat(extensoesCsv).includes(tipo)) {
            setErro('O arquivo deve ser uma planilha ou um arquivo CSV')
            return setImports(undefined)
        } else setErro(null)

        const fileUploaded: File = event.target.files[0]
        const reader = new FileReader()

        reader.readAsArrayBuffer(fileUploaded)
        reader.onload = event => {
            if (!event.target?.result) return
            setImports(undefined)
            setLinhaInicial(extensoesCsv.includes(tipo) ? 0 : 1)
            try {
                const funcionarios = extensoesXls.includes(tipo)
                    ? lerXlsFuncionarios(event.target.result)
                    : lerCsvFuncionarios(event.target.result)
                setImports(
                    funcionarios.data.map((f: TFuncionario) =>
                        f
                            ? {
                                  ...f,
                                  erro: !!funcionarios.erros.find(
                                      e => e.id === f.id,
                                  ),
                              }
                            : undefined,
                    ),
                )
                setErro(
                    funcionarios.erros.map(({ message }) => message).join('\n'),
                )
            } catch (e: any) {
                setErro(e.message)
            }
        }
    }

    return (
        <div className="size-full p-4">
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <FormFieldSelect
                        required
                        label="Regra de cálculo"
                        name="regraCalculo"
                        fullWidth
                        options={
                            regras_calculo?.map(regra => ({
                                value: String(regra.id),
                                label: regra.nome,
                            })) ?? []
                        }
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormFieldSelect
                        label="Tabela de horário"
                        name="tabelaHorarioId"
                        fullWidth
                        options={[
                            { value: '', label: 'Nenhuma (Horista)' },
                        ].concat(
                            tabelas_horario?.map(tabela => ({
                                value: String(tabela.id),
                                label: tabela.nome,
                            })) ?? [],
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Endereco noDivider className="-mb-4" />
                </Grid>
                <Grid item xs={12}>
                    <FormFile
                        label="Arquivo"
                        name="arquivo"
                        id="arquivo_field"
                        onChange={handleChange}
                        notPreview
                        clearable
                    />
                </Grid>
                <Grid item xs={12} mt={-4}>
                    {erro && (
                        <Alert
                            className="w-full"
                            severity="error"
                            variant="filled"
                            onClose={() => setErro(null)}
                        >
                            {erro.split('\n').map((e, i) => {
                                return (
                                    <p key={i}>
                                        {e}
                                        <br />
                                    </p>
                                )
                            })}
                        </Alert>
                    )}
                </Grid>
            </Grid>
            <div className="h-[clamp(300px,46vh,600px)] w-full">
                <ContentGuide.DataGrid
                    loading={false}
                    rows={
                        funcionariosImportar?.length
                            ? funcionariosImportar.filter(Boolean)
                            : []
                    }
                    columns={[
                        {
                            field: 'matricula',
                            headerName: 'Matrícula',
                            width: 100,
                        },
                        {
                            field: 'nome',
                            headerName: 'Nome',
                            flex: 1,
                            minWidth: 200,
                        },
                        {
                            field: 'cargo',
                            headerName: 'Cargo/Função',
                            width: 120,
                        },
                        {
                            field: 'pis',
                            headerName: 'PIS/PASEP',
                            width: 130,
                        },
                        { field: 'cpf', headerName: 'CPF', width: 130 },
                        {
                            field: 'dataAbnt',
                            headerName: 'Data de admissão',
                            width: 130,
                        },
                    ]}
                    getRowClassName={params => {
                        if (params.row.erro) return 'row-error'
                        else if (params.row.importado) return 'row-success'
                    }}
                    ToolbarProps={{ hideToolbar: true }}
                />
            </div>
        </div>
    )
}
